import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import axiosInstance from '../../../helpers/axios';
import { formatCurrency, formatPercent } from '../utils/format';
import { useInstituteOptions } from '../hooks/useInstituteOptions';
import { MONTH_NAMES } from '../utils/month';
import { fibeTokens } from '../../../theme';
import PaginationTable from '../../status/PaginationTable';

const SINGLE_MILESTONE_TENURE_THRESHOLD = 12;
const PAGE_SIZE = 15;

const MODEL_BADGES = {
    subvention: { badgeClass: 'badge-blue', label: 'Subvention' },
    interest: { badgeClass: 'badge-gray', label: 'Interest' },
};

const STATUS_BADGES = {
    calculated: { badgeClass: 'badge-green', label: 'Calculated' },
    pending_approval: { badgeClass: 'badge-yellow', label: 'Pending Approval' },
    pending_reconciliation: { badgeClass: 'badge-gray', label: 'Pending Reconciliation' },
    error: { badgeClass: 'badge-red', label: 'Error' },
};

const EMPTY_FILTERS = { instituteId: '', loanCategory: '', commissionStatus: '', month: '', year: '', search: '' };

const FILTER_MENU_PROPS = {
    PaperProps: {
        sx: {
            '& .MuiMenuItem-root': {
                fontFamily: fibeTokens.font.family,
                fontSize: 12.5,
                fontWeight: 400,
                minHeight: 'auto',
                padding: '8px 12px',
            },
        },
    },
};

function payoutTypeBadge(numEmi) {
    return numEmi <= SINGLE_MILESTONE_TENURE_THRESHOLD
        ? { badgeClass: 'badge-green', label: '100% Upfront' }
        : { badgeClass: 'badge-gray', label: '50/25/25' };
}

const BADGE_CHIP_COLOR = {
    'badge-green': 'success',
    'badge-yellow': 'warning',
    'badge-red': 'error',
    'badge-gray': 'default',
    'badge-blue': 'info',
};

const SKELETON_COLUMNS = [
    { width: 62 },
    { width: 62 },
    { width: 118 },
    { width: 64, pill: true },
    { width: 28 },
    { width: 78, pill: true },
    { width: 64 },
    { width: 64 },
    { width: 32 },
    { width: 46 },
    { width: 64 },
    { width: 76, pill: true },
];

function RefreshGlyph() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v5h-5" />
        </svg>
    );
}

function SearchGlyph() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
        </svg>
    );
}

function PercentGlyph() {
    return (
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="5" x2="5" y2="19" />
            <circle cx="6.5" cy="6.5" r="2.5" />
            <circle cx="17.5" cy="17.5" r="2.5" />
        </svg>
    );
}

function PageHeader({ isLoading, onRefresh, subtitle }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
            <Box>
                <Box
                    component="h1"
                    sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.pageTitle, color: fibeTokens.color.textPrimary }}
                >
                    Commission Tracker
                </Box>
                <Box sx={{ margin: '5px 0 0', fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper }}>
                    {subtitle}
                </Box>
            </Box>
            <Button
                variant="fibeSecondary"
                disabled={isLoading}
                onClick={onRefresh}
                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshGlyph />}
            >
                {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
        </Box>
    );
}

function PeriodWarningBanner() {
    return (
        <Box
            sx={{
                background: fibeTokens.color.warningTint,
                border: `1px solid ${fibeTokens.color.warning}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                padding: fibeTokens.density.cardPadding,
                marginBottom: '14px',
                fontFamily: fibeTokens.font.family,
                fontWeight: 500,
                fontSize: 12.5,
                color: fibeTokens.color.warning,
            }}
        >
            Select both Month and Year to filter by period — with only one set, the period filter is ignored.
        </Box>
    );
}

function ErrorBanner({ message, onRetry }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '14px',
                background: fibeTokens.color.dangerTint,
                border: `1px solid ${fibeTokens.color.dangerBorder}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                padding: fibeTokens.density.cardPadding,
            }}
        >
            <Box>
                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.danger }}>
                    Could not load commissions
                </Box>
                <Box sx={{ ...fibeTokens.font.helper, fontFamily: fibeTokens.font.family, color: fibeTokens.color.danger, marginTop: '4px' }}>
                    {message}
                </Box>
            </Box>
            <Box sx={{ flex: 'none' }}>
                <Button variant="fibeSecondary" size="small" onClick={onRetry}>Retry</Button>
            </Box>
        </Box>
    );
}

function CommissionsEmptyState({ hasActiveFilters, onClearFilters }) {
    return (
        <Box sx={{ textAlign: 'center', padding: '40px 18px' }}>
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    margin: '0 auto 12px',
                    borderRadius: '12px',
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: fibeTokens.color.textDisabled,
                }}
            >
                <PercentGlyph />
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.textPrimary }}>
                {hasActiveFilters ? 'No matches for these filters' : 'No commissions tracked yet'}
            </Box>
            <Box
                sx={{
                    fontFamily: fibeTokens.font.family,
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: fibeTokens.color.textMuted,
                    margin: '5px 0 13px',
                }}
            >
                {hasActiveFilters
                    ? 'Try widening or clearing the filters above.'
                    : 'Commissions appear here once loans are reconciled against a Fibe disbursement file.'}
            </Box>
            {hasActiveFilters && (
                <Button variant="fibePrimary" size="small" onClick={onClearFilters}>Clear filters</Button>
            )}
        </Box>
    );
}

function CommissionsTableSkeleton() {
    return (
        <>
            <Box
                sx={{
                    height: `${fibeTokens.density.tableHeaderHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    background: fibeTokens.color.rowHover,
                    borderBottom: `1px solid ${fibeTokens.color.border}`,
                }}
            >
                <Skeleton variant="text" width={160} height={10} />
            </Box>
            {Array.from({ length: 6 }).map((_, rowIndex) => (
                <Box
                    key={rowIndex}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        height: `${fibeTokens.density.tableRowHeight}px`,
                        padding: '0 14px',
                        borderBottom: rowIndex < 5 ? `1px solid ${fibeTokens.color.border}` : 'none',
                    }}
                >
                    {SKELETON_COLUMNS.map((col, colIndex) => (
                        <Skeleton
                            key={colIndex}
                            variant={col.pill ? 'rounded' : 'text'}
                            width={col.width}
                            height={col.pill ? 18 : 12}
                            sx={{ borderRadius: col.pill ? '999px' : undefined, flex: 'none' }}
                        />
                    ))}
                </Box>
            ))}
        </>
    );
}

function CommissionsTab() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [commissions, setCommissions] = useState([]);
    const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    const { institutes } = useInstituteOptions();
    const instituteNameById = useMemo(() => {
        const map = new Map();
        institutes.forEach((institute) => map.set(institute.id, institute.brandName));
        return map;
    }, [institutes]);

    const updateFilter = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        setPage(1);
    };

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(filters.search), 300);
        return () => clearTimeout(handle);
    }, [filters.search]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const isPeriodFilterIncomplete = Boolean(filters.month) !== Boolean(filters.year);

    const loadCommissions = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);

        axiosInstance
            .get('/fibe-billing/commissions', {
                params: {
                    page,
                    limit: PAGE_SIZE,
                    instituteId: filters.instituteId || undefined,
                    loanCategory: filters.loanCategory || undefined,
                    commissionStatus: filters.commissionStatus || undefined,
                    month: filters.month || undefined,
                    year: filters.year || undefined,
                    search: debouncedSearch || undefined,
                },
            })
            .then((response) => {
                setCommissions(response.data.data);
                setMeta(response.data.meta);
            })
            .catch(() => setErrorMessage('Could not load commissions.'))
            .finally(() => setIsLoading(false));
    }, [page, filters.instituteId, filters.loanCategory, filters.commissionStatus, filters.month, filters.year, debouncedSearch]);

    useEffect(() => {
        loadCommissions();
    }, [loadCommissions]);

    const clearFilters = () => {
        setFilters({ ...EMPTY_FILTERS });
        setPage(1);
    };
    const hasActiveFilters = Object.values(filters).some(Boolean);
    const commissionCountLabel = `${meta.total} commission${meta.total === 1 ? '' : 's'} found`;

    return (
        <div>
            <PageHeader isLoading={isLoading} onRefresh={loadCommissions} subtitle={commissionCountLabel} />

            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: fibeTokens.density.cardShadow,
                    padding: '12px 14px',
                    display: 'flex',
                    gap: '9px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '14px',
                }}
            >
                <Box
                    component="span"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontFamily: fibeTokens.font.family,
                        ...fibeTokens.font.kpiLabel,
                        paddingRight: '4px',
                    }}
                >
                    Filters
                </Box>

                <Select
                    size="fibeFlatSm"
                    displayEmpty
                    value={filters.instituteId}
                    onChange={(e) => updateFilter('instituteId', e.target.value)}
                    MenuProps={FILTER_MENU_PROPS}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All Institutes</MenuItem>
                    {institutes.map((institute) => (
                        <MenuItem key={institute.id} value={institute.id}>{institute.brandName}</MenuItem>
                    ))}
                </Select>

                <Select
                    size="fibeFlatSm"
                    displayEmpty
                    value={filters.loanCategory}
                    onChange={(e) => updateFilter('loanCategory', e.target.value)}
                    MenuProps={FILTER_MENU_PROPS}
                    sx={{ minWidth: 120 }}
                >
                    <MenuItem value="">All Models</MenuItem>
                    <MenuItem value="subvention">Subvention</MenuItem>
                    <MenuItem value="interest">Interest</MenuItem>
                </Select>

                <Select
                    size="fibeFlatSm"
                    displayEmpty
                    value={filters.commissionStatus}
                    onChange={(e) => updateFilter('commissionStatus', e.target.value)}
                    MenuProps={FILTER_MENU_PROPS}
                    sx={{ minWidth: 165 }}
                >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="calculated">Calculated</MenuItem>
                    <MenuItem value="pending_approval">Pending Approval</MenuItem>
                    <MenuItem value="pending_reconciliation">Pending Reconciliation</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                </Select>

                <Select
                    size="fibeFlatSm"
                    displayEmpty
                    value={filters.month}
                    onChange={(e) => updateFilter('month', e.target.value)}
                    MenuProps={FILTER_MENU_PROPS}
                    sx={{ minWidth: 120 }}
                >
                    <MenuItem value="">All Months</MenuItem>
                    {MONTH_NAMES.map((name, index) => (
                        <MenuItem key={name} value={String(index + 1)}>{name}</MenuItem>
                    ))}
                </Select>

                <OutlinedInput
                    size="fibeFlatSm"
                    type="number"
                    placeholder="Year"
                    value={filters.year}
                    onChange={(e) => updateFilter('year', e.target.value)}
                    sx={{ width: 84 }}
                />

                <OutlinedInput
                    size="fibeFlatSm"
                    placeholder="Search App ID or Loan ID..."
                    value={filters.search}
                    onChange={(e) => updateFilter('search', e.target.value)}
                    startAdornment={(
                        <InputAdornment position="start">
                            <Box sx={{ display: 'flex', color: fibeTokens.color.textDisabled }}>
                                <SearchGlyph />
                            </Box>
                        </InputAdornment>
                    )}
                    sx={{ flex: 1, minWidth: 200 }}
                />

                <Box sx={{ width: '1px', height: '20px', background: fibeTokens.color.border, flex: 'none' }} />

                <Button variant="fibeGhost" size="small" disabled={isLoading} onClick={clearFilters}>
                    Clear all
                </Button>
            </Box>

            {isPeriodFilterIncomplete && <PeriodWarningBanner />}

            <Box
                sx={{
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    boxShadow: fibeTokens.density.cardShadow,
                    overflow: 'hidden',
                }}
            >
                {isLoading ? (
                    <CommissionsTableSkeleton />
                ) : errorMessage ? (
                    <Box sx={{ padding: '16px' }}>
                        <ErrorBanner message={errorMessage} onRetry={loadCommissions} />
                    </Box>
                ) : commissions.length === 0 ? (
                    <CommissionsEmptyState hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters} />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table sx={{ width: '100%', minWidth: '1500px', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        size="fibe"
                                        variant="head"
                                        align="left"
                                        sx={{ position: 'sticky', left: 0, zIndex: 1, width: 130, background: fibeTokens.color.rowHover, boxShadow: '2px 0 4px -2px rgba(16,24,40,.10)' }}
                                    >
                                        App ID
                                    </TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 130 }}>Loan ID</TableCell>
                                    <TableCell size="fibe" variant="head" align="left">Institute</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 130 }}>Model</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 80 }}>Tenure</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: 140 }}>Payout Type</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 110 }}>Order Amt</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 120 }}>Total Income</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 90 }}>IRR</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 100 }}>FeeMonk %</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: 120 }}>Commission</TableCell>
                                    <TableCell
                                        size="fibe"
                                        variant="head"
                                        align="left"
                                        sx={{ position: 'sticky', right: 0, zIndex: 1, width: 130, background: fibeTokens.color.rowHover, boxShadow: '-2px 0 4px -2px rgba(16,24,40,.10)' }}
                                    >
                                        Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {commissions.map((commission) => {
                                    const modelBadge = MODEL_BADGES[commission.loanCategory] ?? { badgeClass: 'badge-gray', label: commission.loanCategory };
                                    const statusBadge = STATUS_BADGES[commission.commissionStatus] ?? { badgeClass: 'badge-gray', label: commission.commissionStatus };
                                    const payoutBadge = payoutTypeBadge(commission.numEmi);
                                    return (
                                        <TableRow key={commission.id} hover>
                                            <TableCell
                                                size="fibe"
                                                variant="body"
                                                align="left"
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                    background: fibeTokens.color.surface,
                                                    boxShadow: '2px 0 4px -2px rgba(16,24,40,.10)',
                                                }}
                                            >
                                                {commission.applicationId}
                                            </TableCell>
                                            <TableCell
                                                size="fibe"
                                                variant="body"
                                                align="left"
                                            >
                                                {commission.fibeLoanId ?? '—'}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="left">
                                                {commission.instituteName ?? instituteNameById.get(commission.instituteId) ?? commission.instituteId}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="left">
                                                <Chip variant="fibePill" color={BADGE_CHIP_COLOR[modelBadge.badgeClass] ?? 'default'} label={modelBadge.label} />
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right">{commission.numEmi}m</TableCell>
                                            <TableCell size="fibe" variant="body" align="left">
                                                <Chip variant="fibePill" color={BADGE_CHIP_COLOR[payoutBadge.badgeClass] ?? 'default'} label={payoutBadge.label} />
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatCurrency(commission.orderAmount)}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatCurrency(commission.totalIncome)}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatPercent(commission.irr)}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {commission.feemonkSharePct != null ? formatPercent(commission.feemonkSharePct) : '—'}
                                            </TableCell>
                                            <TableCell
                                                size="fibe"
                                                variant="body"
                                                align="right"
                                                sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: fibeTokens.color.textPrimary }}
                                            >
                                                {commission.commissionAmount != null ? formatCurrency(commission.commissionAmount) : '—'}
                                            </TableCell>
                                            <TableCell
                                                size="fibe"
                                                variant="body"
                                                align="left"
                                                sx={{
                                                    position: 'sticky',
                                                    right: 0,
                                                    zIndex: 1,
                                                    background: fibeTokens.color.surface,
                                                    boxShadow: '-2px 0 4px -2px rgba(16,24,40,.10)',
                                                }}
                                            >
                                                <Chip variant="fibePill" color={BADGE_CHIP_COLOR[statusBadge.badgeClass] ?? 'default'} label={statusBadge.label} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>

            {!errorMessage && meta.total > 0 && (
                <Box sx={{ marginTop: '14px' }}>
                    <PaginationTable
                        key={`${filters.instituteId}|${filters.loanCategory}|${filters.commissionStatus}|${filters.month}|${filters.year}|${debouncedSearch}`}
                        startPage={page}
                        rowsPerPage={PAGE_SIZE}
                        totalRows={meta.total}
                        paginate={setPage}
                    />
                </Box>
            )}
        </div>
    );
}

export default CommissionsTab;
